// Import utilities
import { isDefined, isEmpty, getUniqueID } from '@automator/utilities';

// Import styles
const styles = require( '../base/web-component-style.scss' );

// Base template
const template = document.createElement( 'template' );
template.innerHTML = `
    <style>

        /* Base styles */
        ${styles}

    </style>

    <div class="wrapper">
        <div class="control"></div>
    </div>

    <slot name="hidden-field"></slot>
`;

/**
 * Creates a Base class to handle input web components
 *
 * @param  superclass
 */
const InputBase = ( superclass ) =>
	class extends superclass {
		constructor() {
			// Call to the constructor of the parent class
			super();

			// Attach shadow
			this.attachShadow( {
				mode: 'open',
			} );

			// Create alert
			this.shadowRoot.appendChild( template.content.cloneNode( true ) );

			// Make sure it has an ID
			this.#checkFieldID();

			// Create a hidden field so the input can be used in a form
			this.#handleForm();
		}

		/**
		 * Returns the observed attributes in all inputs
		 */
		static get baseComponentAttributes() {
			return {
				id: {
					type: 'string',
				},
				label: {
					type: 'string',
				},
				helper: {
					type: 'string',
				},
				required: {
					type: 'boolean',
				},
				disabled: {
					type: 'boolean',
				},
				hidden: {
					type: 'boolean',
				},
			};
		}

		/**
		 * Defines observed attributes
		 *
		 * @return {Array} Observed attributes
		 */
		static get observedAttributes() {
			// Extend base observed attributes
			return Object.keys( this.componentAttributes );
		}

		/**
		 * Validates the field
		 *
		 * @return {Boolean} TRUE if it's valid
		 */
		reportValidity() {
			// Check if the field is valid
			const isValid = this.$input.reportValidity();

			// Add class to the wrapper
			if ( ! isValid ) {
				this.$wrapper.classList.add( 'error' );
			} else {
				this.$wrapper.classList.remove( 'error' );
			}

			// If it isn't valid, focus the field
			if ( ! isValid ) {
				this.$input.focus();
			}

			// If it isn't valid, scroll to the field
			if ( ! isValid ) {
				this.scrollIntoView( { behavior: 'smooth', block: 'end' } );
			}

			return isValid;
		}

		/**
		 * Callback invoked when the attribute changes
		 *
		 * @param {String} attrName Attribute name
		 * @param {String} oldValue The old value
		 * @param {String} newValue The new value
		 * @return {undefined}
		 */
		attributeChangedCallback( attrName, oldValue, newValue ) {
			// Check if the old value is different than the new one
			// This will prevent an infinite loop
			if ( oldValue !== newValue ) {
				// Get the attribute data
				const changedAttribute = this.constructor.componentAttributes[attrName];

				// Get property name
				const propertyKey = isDefined( changedAttribute.propertyKey ) ? changedAttribute.propertyKey : attrName;

				// Check if it's a boolean attribute
				if ( changedAttribute.type == 'boolean' ) {
					this[propertyKey] = this.hasAttribute( attrName );
				} else {
					this[propertyKey] = this.getAttribute( attrName );
				}
			}
		}

		/**
		 * Checks if the field has an ID. If it doesn't, it assigns one
		 *
		 * @return {undefined}
		 */
		#checkFieldID() {
			// Check if it has an ID
			if ( isEmpty( this.id ) ) {
				// Create a random ID
				this.id = `uo-input-${getUniqueID()}`;
			}
		}

		/**
		 * Creates a hidden field so the input can be used in a form
		 *
		 * @return {undefined}
		 */
		#handleForm() {
			// Check if it's inside a form
			if ( this.closest( 'form' ) ) {
				// Get the form
				const $form = this.closest( 'form' );

				// Create the hidden field in the Light DOM
				this.insertAdjacentHTML(
					'beforeEnd',
					`
                <input
                    type="hidden"
                    name="${this.id}"
                    slot="hidden-field"
                >
            `
				);

				// Update it automatically when the web component changes
				this.addEventListener( 'change', () => {
					// Update the hidden field
					this.updateHiddenField();
				} );

				// Validate the field on submission
				$form.addEventListener( 'submit', ( event ) => {
					// Validate the fields
					if ( ! this.reportValidity() ) {
						// Prevent submission
						event.preventDefault();
					}
				} );
			}
		}

		/**
		 * Updates hidden field
		 *
		 * @return {undefined}
		 */
		updateHiddenField() {
			// Update the hidden field
			this.#$hiddenField.value = this.value;
		}

		/**
		 * Returns the id
		 *
		 * @return {String} The id
		 */
		get id() {
			return this.getAttribute( 'id' );
		}

		/**
		 * Sets the id
		 *
		 * @param {String} id The id
		 * @return {undefined}
		 */
		set id( value ) {
			if ( value ) {
				// Set id in the host
				this.setAttribute( 'id', value );
			} else {
				// Set id in the host
				this.removeAttribute( 'id' );
			}
		}

		/**
		 * Returns the label
		 *
		 * @return {String} The label
		 */
		get label() {
			return this.getAttribute( 'label' );
		}

		/**
		 * Sets the label
		 *
		 * @param {String} label The label
		 * @return {undefined}
		 */
		set label( value ) {
			if ( value ) {
				// Set label in the host
				this.setAttribute( 'label', value );

				// Creates a label in the element
				this.#createLabelElement( value );
			} else {
				// Set label in the host
				this.removeAttribute( 'label' );

				// Remove the label element
				this.#removeLabelElement();
			}
		}

		/**
		 * Creates the label in the DOM
		 *
		 * @param {String} label The label
		 * @return {undefined}
		 */
		#createLabelElement( label ) {
			// Check if there is already a label element
			if ( isDefined( this.$label ) ) {
				// Update the content
				this.$label.querySelector( 'span' ).innerHTML = label;
			} else {
				// Create the element
				const $label = document.createElement( 'label' );

				// Add class
				$label.classList.add( 'top-label' );

				// Set the "for" parameter
				$label.htmlFor = this.id;

				// Create span inside. It will help us style things better
				$label.insertAdjacentHTML(
					'beforeEnd',
					`
                <span>${label}</span>
            `
				);

				// Creates a label in the element
				this.$wrapper.insertAdjacentElement( 'afterBegin', $label );
			}
		}

		/**
		 * Removes the label from the DOM
		 *
		 * @return {undefined}
		 */
		#removeLabelElement() {
			// Check if there is a label
			if ( isDefined( this.$label ) ) {
				// Remove element
				this.$label.parentNode.removeChild( this.$label );
			}
		}

		/**
		 * Returns the helper
		 *
		 * @return {String} The helper
		 */
		get helper() {
			return this.getAttribute( 'helper' );
		}

		/**
		 * Sets the helper
		 *
		 * @param {String} value The helper
		 * @return {undefined}
		 */
		set helper( value ) {
			if ( value ) {
				// Set helper in the host
				this.setAttribute( 'helper', value );

				// Creates a helper in the element
				this.#createHelperElement( value );
			} else {
				// Set helper in the host
				this.removeAttribute( 'helper' );

				// Remove the helper element
				this.#removeHelperElement();
			}
		}

		/**
		 * Creates the helper in the DOM
		 *
		 * @param {String} helper The helper
		 * @return {undefined}
		 */
		#createHelperElement( helper ) {
			// Check if there is already a helper element
			if ( isDefined( this.$helper ) ) {
				// Update the content
				this.$helper.innerHTML = helper;
			} else {
				// Create the element
				const $helper = document.createElement( 'div' );
				$helper.classList.add( 'helper' );

				// Create span inside. It will help us style things better
				$helper.insertAdjacentHTML(
					'beforeEnd',
					`
                <span>${helper}</span>
            `
				);

				// Creates a helper in the element
				this.$wrapper.insertAdjacentElement( 'beforeEnd', $helper );
			}
		}

		/**
		 * Removes the helper from the DOM
		 *
		 * @return {undefined}
		 */
		#removeHelperElement() {
			// Check if there is a helper
			if ( isDefined( this.$helper ) ) {
				// Remove element
				this.$helper.parentNode.removeChild( this.$helper );
			}
		}

		/**
		 * Checks whether the input connected to the label is required
		 *
		 * @return {Boolean} True if it's required
		 */
		get required() {
			return this.hasAttribute( 'required' );
		}

		/**
		 * Sets whether the input connected to the label is required
		 *
		 * @param {Boolean} value TRUE if the input connected to the label is required
		 * @return {undefined}
		 */
		set required( value ) {
			if ( value ) {
				// Set required status in the host
				this.setAttribute( 'required', '' );
			} else {
				// Set required status in the host
				this.removeAttribute( 'required' );
			}
		}

		/**
		 * Checks whether the input connected to the label is disabled
		 *
		 * @return {Boolean} True if it's disabled
		 */
		get disabled() {
			return this.hasAttribute( 'disabled' );
		}

		/**
		 * Sets whether the input connected to the label is disabled
		 *
		 * @param {Boolean} value TRUE if the input connected to the label is disabled
		 * @return {undefined}
		 */
		set disabled( value ) {
			if ( value ) {
				// Set disabled status in the host
				this.setAttribute( 'disabled', '' );
			} else {
				// Set disabled status in the host
				this.removeAttribute( 'disabled' );
			}
		}

		/**
		 * Checks whether the input is hidden
		 *
		 * @return {Boolean} True if it's hidden
		 */
		get hidden() {
			return this.hasAttribute( 'hidden' );
		}

		/**
		 * Sets whether the input is hidden
		 *
		 * @param {Boolean} value TRUE if the input is hidden
		 * @return {undefined}
		 */
		set hidden( value ) {
			if ( value ) {
				// Set hidden status in the host
				this.setAttribute( 'hidden', '' );
			} else {
				// Set hidden status in the host
				this.removeAttribute( 'hidden' );
			}
		}

		/**
		 * Returns the input wrapper
		 *
		 * @return {Node} The wrapper
		 */
		get $wrapper() {
			return this.shadowRoot.querySelector( '.wrapper' );
		}

		/**
		 * Returns the control wrapper
		 *
		 * @return {Node} The control wrapper
		 */
		get $controlWrapper() {
			return this.shadowRoot.querySelector( '.control' );
		}

		/**
		 * Get the label element
		 *
		 * @return {Node} The label element
		 */
		get $label() {
			return this.shadowRoot.querySelector( 'label.top-label' );
		}

		/**
		 * Get the helper element
		 *
		 * @return {Node} The helper element
		 */
		get $helper() {
			return this.shadowRoot.querySelector( '.helper' );
		}

		/**
		 * Get the hidden field element
		 *
		 * @return {Node} The hidden field element
		 */
		get #$hiddenField() {
			return this.querySelector( `:scope > input[name="${this.id}"]` );
		}
	};

export default InputBase;
